import { LinearProgress, Paper, Stack, Typography } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

const messages = defineMessages({
  allocated: {
    id: 'patientPlans.seatsAllocated',
    defaultMessage: 'Patient seats allocated',
  },
  ofTotal: {
    id: 'patientPlans.seatsOfTotal',
    defaultMessage: 'of {total}',
  },
  unassigned: {
    id: 'patientPlans.seatsUnassigned',
    defaultMessage: '{count} seats unassigned',
  },
  overAllocated: {
    id: 'patientPlans.seatsOverAllocated',
    defaultMessage: '{count} seats over the pool',
  },
  noSeats: {
    id: 'patientPlans.noSeatsSet',
    defaultMessage: 'No patient seats set',
  },
});

const PANEL_WIDTH = 260;

interface PatientSeatsSummaryProps {
  /** The organization's patient seat pool */
  total: number;
  /** Seats handed out across every plan */
  allocated: number;
}

/**
 * Live tally of the seat pool as the admin assigns seats to plans.
 *
 * Uses `LinearProgress` directly rather than `@components/Progress`, which
 * always renders a percentage beside the bar that this panel does not show.
 */
export default function PatientSeatsSummary({
  total,
  allocated,
}: PatientSeatsSummaryProps) {
  const { formatMessage } = useIntl();

  const remaining = total - allocated;
  const overAllocated = remaining < 0;
  // A pool of 0 has no ratio to show, and an over-allocated bar stays full
  // rather than running past its track.
  const percent = total > 0 ? Math.min((allocated / total) * 100, 100) : 0;

  return (
    <Paper
      data-testid="patient-seats-summary"
      // `MuiPaper.styleOverrides.root` in src/theme/index.ts sets `border: none`
      // and a 16px radius, which lands after the `outlined` variant and cancels
      // it. Not redundant — do not remove.
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
        width: PANEL_WIDTH,
      }}
      variant="outlined"
    >
      <Stack spacing={1}>
        <Typography color="text.secondary" component="h3" variant="overline">
          {formatMessage(messages.allocated)}
        </Typography>
        {/* Nested rather than two siblings so the text reads "12 of 250"
            to a screen reader, not "12of 250". */}
        <Typography
          color={overAllocated ? 'error' : 'text.primary'}
          data-testid="patient-seats-allocated"
          variant="h4"
        >
          {allocated}{' '}
          <Typography color="text.secondary" component="span" variant="h6">
            {formatMessage(messages.ofTotal, { total })}
          </Typography>
        </Typography>
        <LinearProgress
          aria-label={formatMessage(messages.allocated)}
          color={overAllocated ? 'error' : 'primary'}
          value={percent}
          variant="determinate"
        />
        <Typography
          color={overAllocated ? 'error' : 'text.secondary'}
          data-testid="patient-seats-remaining"
          variant="body2"
        >
          {/* A pool of 0 has no seats to be unassigned — say so rather than
              reporting "0 seats unassigned", which reads as fully allocated. */}
          {(() => {
            if (overAllocated) {
              return formatMessage(messages.overAllocated, {
                count: -remaining,
              });
            }
            return total > 0
              ? formatMessage(messages.unassigned, { count: remaining })
              : formatMessage(messages.noSeats);
          })()}
        </Typography>
      </Stack>
    </Paper>
  );
}
